import React, { useEffect, useState } from "react";
import axios from "axios";
import { Pencil, Trash2, Eye, Plus, Search, User, Calendar, X } from "lucide-react";
import { uploadImage } from "../../components/common/uploadImage";
import toast from "react-hot-toast";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';


const BlogAdmin = () => {
  const [blogs, setBlogs] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [search, setSearch] = useState("");
  const [imagePreview, setImagePreview] = useState(null);
  const [imageUploading, setImageUploading] = useState(false);

  const [form, setForm] = useState({
    title: "",
    content: "",
    author: "Admin",
    image: "",
    slug: "",
  });

  // ================= FETCH BLOGS =================
  const fetchBlogs = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/blog/get-all`);
      setBlogs(res.data || []);
    } catch (err) {
      toast.error("Failed to load blogs");
    }
  };

  useEffect(() => {
    fetchBlogs();
  }, []);

  // ================= HANDLE TEXT INPUT =================
  const handleChange = (e) => {
    const { name, value } = e.target;
   if (name === "title") {
  const slug = value
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-");

  setForm(prev => ({
    ...prev,
    title: value,
    slug
  }));
} else {
      setForm({ ...form, [name]: value });
    }
  };

  // ================= HANDLE IMAGE UPLOAD =================
  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Show local preview immediately
    const preview = URL.createObjectURL(file);
    setImagePreview(preview);
    setImageUploading(true);

    try {
      const url = await uploadImage(file);
      setForm((prev) => ({ ...prev, image: url }));
      setImagePreview(url);
      toast.success("Image uploaded!");
    } catch {
      toast.error("Image upload failed");
      setImagePreview(null);
    } finally {
      setImageUploading(false);
    }
  };

  // ================= SUBMIT =================
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (imageUploading) {
      toast.error("Please wait for the image to finish uploading");
      return;
    }
    setLoading(true);

    try {

      const slug = editing?.slug || form.title
  .toLowerCase()
  .trim()
  .replace(/[^\w\s-]/g, "")
  .replace(/\s+/g, "-");


      const payload = {
        title: form.title,
        content: form.content,
        author: form.author || "Admin",
        image: form.image || editing?.image || "",
        slug,
      };

      if (editing) {
        // Don't update slug on edit — it's a unique index and URLs would break
        const { slug: _slug, ...editPayload } = payload;
        await axios.put(`${process.env.REACT_APP_API_URL}/blog/edit/${editing._id}`, editPayload);
        toast.success("Blog updated!");
      } else {
        await axios.post(`${process.env.REACT_APP_API_URL}/blog/create`, payload);
        toast.success("Blog created!");
      }

      setShowModal(false);
      setEditing(null);
      setImagePreview(null);
      setForm({ title: "", content: "", author: "Admin", image: "", slug: "" });
      fetchBlogs();
    } catch (err) {
      toast.error("Error saving blog");
    } finally {
      setLoading(false);
    }
  };

  // ================= DELETE =================
  const handleDelete = async () => {
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/blog/delete/${deleteTarget._id}`);
      toast.success("Blog deleted");
      setDeleteTarget(null);
      fetchBlogs();
    } catch {
      toast.error("Failed to delete blog");
    }
  };

  // ================= EDIT =================
  const handleEdit = (blog) => {
    setEditing(blog);
    setForm({
      title: blog.title,
      content: blog.content,
      author: blog.author || "Admin",
      image: blog.image || "",
      slug: blog.slug || "",
    });
    setImagePreview(blog.image || null);
    setShowModal(true);
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ title: "", content: "", author: "Admin", image: "", slug: "" });
    setImagePreview(null);
    setShowModal(true);
  };

  const filtered = blogs.filter(
    (b) =>
      b.title.toLowerCase().includes(search.toLowerCase()) ||
      b.author?.toLowerCase().includes(search.toLowerCase())
  );

  const today = new Date().toLocaleDateString("en-US", {
    month: "short", day: "numeric", year: "numeric",
  });

  const ColorWhitelist = [
  "#000000",
  "#ef4444", // red
  "#f59e0b", // yellow
  "#10b981", // green
  "#3b82f6", // blue
  "#6366f1", // indigo
  "#ec4899"  // pink
];

  const modules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    [{ font: [] }],
    [{ size: ["small", false, "large", "huge"] }],

    ["bold", "italic", "underline", "strike"],

    [{ color: ColorWhitelist }, { background: ColorWhitelist }], // 🎨 colors

    [{ script: "sub" }, { script: "super" }],

    [{ list: "ordered" }, { list: "bullet" }],
    [{ indent: "-1" }, { indent: "+1" }],

    [{ align: [] }],

    ["link", "image", "video"],

    ["clean"]
  ]
};

const formats = [
  "header", "font", "size",
  "bold", "italic", "underline", "strike",
  "color", "background",
  "script",
  "list", "bullet", "indent",
  "align",
  "link", "image", "video"
];



  return (
    <div className="min-h-screen bg-gray-50 font-sans">

      {/* Header */}
      <div className="bg-white px-8 py-6 flex items-center justify-between border-b border-gray-200">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 text-xl">
            📝
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Blog Manager</h1>
            <p className="text-sm text-gray-500">Manage all blog posts from one place</p>
          </div>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-5 py-2.5 rounded-xl transition-colors text-sm"
        >
          <Plus size={16} />
          Add Blog
        </button>
      </div>

      <div className="px-8 py-6">

        {/* Search Bar */}
        <div className="bg-white rounded-xl border border-gray-200 px-4 py-3 flex items-center gap-3 mb-6">
          <Search size={16} className="text-gray-400 flex-shrink-0" />
          <input
            type="text"
            placeholder="Search by title or author..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 outline-none text-sm text-gray-700 placeholder-gray-400"
          />
          {search && (
            <button onClick={() => setSearch("")} className="text-gray-400 hover:text-gray-600 transition-colors">
              <X size={15} />
            </button>
          )}
        </div>

        {/* Blog Cards Grid */}
        {filtered.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 py-20 text-center">
            <div className="text-5xl mb-4">📝</div>
            <p className="font-medium text-gray-500">No blogs found</p>
            <p className="text-sm text-gray-400 mt-1">Try adjusting your search or create a new blog</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((blog) => (
              <div
                key={blog._id}
                className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
              >
                {/* Image */}
                <div className="h-44 bg-gray-100 overflow-hidden">
                  <img
                    src={blog.image || "https://placehold.co/600x400?text=No+Image"}
                    alt={blog.title}
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Content */}
                <div className="p-5">
                  <h2 className="font-bold text-gray-900 text-base mb-2 line-clamp-2 leading-snug">
                    {blog.title}
                  </h2>
                  
                  <div className="text-gray-500 text-sm line-clamp-2 leading-relaxed mb-4" 
                  dangerouslySetInnerHTML={{__html : blog.content}}
                  />

                  {/* Meta */}
                  <div className="flex items-center justify-between text-xs text-gray-400 mb-3 pt-3 border-t border-gray-100">
                    <span className="flex items-center gap-1.5">
                      <User size={12} />
                      {blog.author || "Admin"}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <Calendar size={12} />
                      {new Date(blog.createdAt).toLocaleDateString("en-US", {
                        month: "short", day: "numeric", year: "numeric",
                      })}
                    </span>
                  </div>

                  {/* Slug */}
                  {blog.slug && (
                    <div className="mb-4 flex items-center gap-1.5 bg-gray-50 border border-gray-100 rounded-lg px-2.5 py-1.5 overflow-hidden">
                      <span className="text-gray-400 text-xs flex-shrink-0">🔗</span>
                      <span className="text-xs text-gray-400 truncate font-mono">{blog.slug}</span>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-2">
                    {/* <button className="flex-1 flex items-center justify-center gap-1.5 bg-gray-50 hover:bg-gray-100 text-gray-600 py-2 rounded-lg text-xs font-medium transition-colors border border-gray-200">
                      <Eye size={13} /> View
                    </button> */}
                    <button
                      onClick={() => handleEdit(blog)}
                      className="flex-1 flex items-center justify-center gap-1.5 bg-blue-50 hover:bg-blue-100 text-blue-600 py-2 rounded-lg text-xs font-medium transition-colors border border-blue-100"
                    >
                      <Pencil size={13} /> Edit
                    </button>
                    <button
                      onClick={() => setDeleteTarget(blog)}
                      className="flex-1 flex items-center justify-center gap-1.5 bg-red-50 hover:bg-red-100 text-red-500 py-2 rounded-lg text-xs font-medium transition-colors border border-red-100"
                    >
                      <Trash2 size={13} /> Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Footer Stats */}
        <div className="mt-5 bg-white rounded-xl border border-gray-200 px-6 py-3 flex items-center justify-between text-sm text-gray-500">
          <span>Total Blogs: <strong className="text-gray-800">{blogs.length}</strong></span>
          {search && <span>Showing: <strong className="text-blue-600">{filtered.length}</strong> results</span>}
          <span>Last Updated: <strong className="text-gray-800">{today}</strong></span>
        </div>
      </div>

      {/* Add / Edit Modal */}
      {showModal && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
          onClick={() => setShowModal(false)}
        >
          <div
            className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
              <h2 className="text-lg font-bold text-gray-900">
                {editing ? "Edit Blog" : "Create Blog"}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
              <div className="px-6 py-5 space-y-4 overflow-y-auto flex-1">

                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      Title *
                    </label>
                    <span className={`text-xs font-medium ${
                      form.title.length < 5 ? "text-red-400" :
                      form.title.length > 150 ? "text-red-400" : "text-gray-400"
                    }`}>
                      {form.title.length} / 150
                    </span>
                  </div>
                  <input
                    name="title"
                    value={form.title}
                    onChange={handleChange}
                    placeholder="e.g. How to grow your business in 2026"
                    required
                    minLength={5}
                    maxLength={150}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-50 transition"
                  />
                  {form.title.length > 0 && form.title.length < 5 && (
                    <p className="text-xs text-red-400 mt-1">Minimum 5 characters required</p>
                  )}
                  {/* Slug preview */}
                  {form.slug && (
                    <div className="mt-2 flex items-center gap-1.5 bg-gray-50 border border-gray-100 rounded-lg px-2.5 py-1.5">
                      <span className="text-gray-400 text-xs flex-shrink-0">🔗 Slug:</span>
                      <span className="text-xs text-gray-500 truncate font-mono">{form.slug}</span>
                    </div>
                  )}
                </div>

                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1.5">
                    Content *
                  </label>

                  <ReactQuill value={form.content} theme="snow"
                        onChange={(value) => {
                          setForm((prev) => ({
                            ...prev,
                            content: value,
                          }));
                        }}
                    modules={modules}
                    formats={formats}
                  />

                </div>

                
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1.5">
                    Author
                  </label>
                  <input
                    name="author"
                    value={form.author}
                    onChange={handleChange}
                    placeholder="e.g. John Doe"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-50 transition"
                  />
                  <p className="text-xs text-gray-400 mt-1">Defaults to "Admin" if left empty</p>
                </div>

                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1.5">
                    Cover Image
                  </label>

                  {/* Image Preview */}
                  {imagePreview && (
                    <div className="relative mb-3 rounded-lg overflow-hidden border border-gray-200 h-36">
                      <img
                        src={imagePreview}
                        alt="preview"
                        className="w-full h-full object-cover"
                      />
                      {imageUploading && (
                        <div className="absolute inset-0 bg-white/70 flex items-center justify-center text-sm text-gray-500">
                          Uploading...
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => { setImagePreview(null); setForm((p) => ({ ...p, image: "" })); }}
                        className="absolute top-2 right-2 bg-white rounded-full p-1 shadow text-gray-500 hover:text-red-500 transition-colors"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  )}

                  <label className="flex items-center justify-center gap-2 border-2 border-dashed border-gray-200 hover:border-blue-300 rounded-lg px-4 py-4 cursor-pointer transition-colors text-sm text-gray-500 hover:text-blue-500">
                    <span>📷</span>
                    {imagePreview ? "Change image" : "Click to upload image"}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="hidden"
                    />
                  </label>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="px-6 py-4 border-t border-gray-100 flex gap-3 justify-end flex-shrink-0">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || imageUploading || form.title.length < 5}
                  className="px-5 py-2 text-sm font-semibold bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition"
                >
                  {loading ? "Saving..." : imageUploading ? "Uploading..." : editing ? "Save Changes" : "Create Blog"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirm Modal */}
      {deleteTarget && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
          onClick={() => setDeleteTarget(null)}
        >
          <div
            className="bg-white rounded-2xl w-full max-w-sm shadow-2xl p-6 text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Trash2 size={22} className="text-red-500" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">Delete Blog?</h3>
            <p className="text-sm text-gray-500 mb-1">
              You're about to delete:
            </p>
            <p className="text-sm font-semibold text-gray-800 mb-6 px-4 line-clamp-2">
              "{deleteTarget.title}"
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="flex-1 px-4 py-2.5 text-sm font-semibold bg-red-500 hover:bg-red-600 text-white rounded-lg transition"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogAdmin;